import { useCallback, ReactNode } from "react"
import { GetApp } from "../../App"
import { GetCruise, GetDay, GetShip, GetTrip } from "../../App/Context"
import { ILog, IStatus, Log } from "../../Types"
import { LogView, LogViewCreateCallback } from "../LogView"

interface DayLogsTimelineViewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  clearHighlight?: any;
  currentLog?: ILog;
  currentLogView?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  highlight?: any; logTabs?: Record<number, string>; logs: ILog[];
  // #1137: the current day and tomorrow can still receive logs later, so their empty
  // timeline keeps the "yet" wording (DayView_logs_empty); every other zero-log day
  // (a backfilled/blank day, or a real day whose only log was later deleted) never
  // will, so it uses the "final" wording (DayView_logs_empty_past) instead.
  isCurrentDay?: boolean;
  isTomorrow?: boolean;
  recordLog?: LogViewCreateCallback;
  statuses: IStatus[];
}

export function DayLogsTimelineView({ clearHighlight, currentLog, currentLogView, highlight, isCurrentDay, isTomorrow, logTabs, logs, recordLog: _recordLog, statuses }: DayLogsTimelineViewProps) {
  const app = GetApp();
  const [_ship, _cruise, _trip, _day] = [GetShip(), GetCruise().cruise, GetTrip(), GetDay().day];

  const preHours = [];
  const postHours = [];
  const logViews = [];

  const logStatuses = useCallback((logId: number): IStatus[] => {
    return statuses.filter((s) => {
      if (logId == 0) {
        return s.endLogId == undefined;
      }
      if (s.endLogId != undefined) {
        return logId >= s.startLogId && logId <= s.endLogId;
      }
      return logId >= s.startLogId;
    });
  }, [statuses]);

  if (logs || currentLog) {
    let allLogs: ILog[] = [];
    if (logs) {
      allLogs = logs.slice();
    }
    if (currentLog) {
      currentLog.nmeaPending = true;
      // A real (persisted, id > 0) currentLog should never also appear in `logs` --
      // it should normally be a distinct id-0 sentinel. Guard against a caller handing
      // back the same committed log for both anyway, which would otherwise render the
      // shared id twice: once as a plain LogView, once as currentLogView. id-0 entries
      // are exempt since several distinct provisional/pending logs can share id 0.
      if (currentLog.id > 0) {
        allLogs = allLogs.filter((l) => l.id !== currentLog.id);
      }
      allLogs.push(currentLog);
    }
    let minHour = undefined;
    let maxHour = undefined;
    let lastHour = -1;
    for (const n in allLogs) {
      const i = typeof n == "number" ? n : parseInt(n);
      const log = allLogs[i];
      let hour = Math.floor(log.time / 3600);
      if (hour > 23) {
        hour = 23;
      }
      if (minHour == undefined || hour < minHour) {
        minHour = hour;
      }
      if (maxHour == undefined || hour > maxHour) {
        maxHour = hour;
      }
      const extraClass = [];
      if (hour > lastHour) {
        extraClass.push("first-of-hour");
      }
      if (i == 0) {
        extraClass.push("first-of-day");
      }
      if (i == allLogs.length - 1) {
        extraClass.push("last-of-day");
      }
      // Identity, never position. Including the array index here made the live
      // current-log row (appended last, and carrying the placeholder id 0) change key
      // every time a committed log arrived, so React unmounted and remounted it --
      // destroying anything transient inside. On a live NMEA feed auto-logs commit
      // continuously, so an open `LogButton` menu vanished mid-interaction and a click
      // could land on a button that had already been detached, silently doing nothing.
      //
      // Committed logs have unique ids; provisional entries share id 0 but carry a
      // `provisionalKey`; the live current log is a singleton with neither.
      const provisionalKey = log.id > 0 ? undefined : Log.parse(log).dataParsed.provisionalKey;
      const rowKey = log.id !== 0
        ? `log-${log.id}`
        : (provisionalKey ? `log-provisional-${provisionalKey}` : "log-current");
      logViews.push(
        <div key={rowKey} className={"DayView-logs-log " + (extraClass.join(" "))} data-log-id={log.id}>
          {(log.nmeaPending && currentLogView) ? currentLogView : (
            <LogView clearHighlight={clearHighlight} initSelected={highlight && highlight == log.id} initTab={logTabs?.[log.id]} log={log} statuses={logStatuses(log.id)} />
          )}
        </div>,
      );
      lastHour = hour;
    }
    if (!minHour) {
      minHour = 0;
    }
    if (!maxHour) {
      maxHour = 0;
    }
    for (let h = 0; h <= minHour; h++) {
      preHours.push(<div key={`pre-hour-${h}`} className={"hour hour-" + h}></div>);
    }
    for (let j = maxHour + 1; j <= 24; j++) {
      postHours.push(<div key={`post-hour-${j}`} className={"hour hour-" + j}></div>);
    }
  }
  if (logViews.length < 1 && !currentLogView) {
    const emptyKey = isCurrentDay || isTomorrow ? "DayView_logs_empty" : "DayView_logs_empty_past";
    logViews.push(<div key="empty" className="DayView-logs-empty">{app.t(emptyKey)}</div>);
  }

  return (
    <div className={"DayView-logs" + (logViews.length < 1 ? " empty" : "")}>
      <div className="DayView-logs-spacer">
        <div className="DayView-logs-timeline pre">
          {preHours}
        </div>
      </div>
      {logViews}
      <div className="DayView-logs-spacer">
        <div className="DayView-logs-timeline post">
          {postHours}
        </div>
      </div>
    </div>
  );
}
